import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface ProgressPayload {
  step: string;
  current?: number;
  total?: number;
  detailLabel?: string;
}

interface GenerationEvent {
  status: string;
  payload?: ProgressPayload & Record<string, unknown>;
}

interface GenerationProgressIndicatorProps {
  /** The current generation event from useGenerationStatus(jobId). */
  event: GenerationEvent | null;
  /** Called when generation completes. */
  onComplete?: (payload: Record<string, unknown>) => void;
  /** Called when generation fails. */
  onFailed?: (error: string) => void;
}

/**
 * Reusable progress indicator for AI generation jobs.
 * Renders spinner (single-step jobs) or progress bar (multi-step batch jobs)
 * based on the ProgressPayload shape.
 */
export function GenerationProgressIndicator({
  event,
  onComplete,
  onFailed,
}: GenerationProgressIndicatorProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime] = useState(() => Date.now());

  useEffect(() => {
    if (!event || event.status === 'complete' || event.status === 'failed') {
      return;
    }
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [event, startTime]);

  useEffect(() => {
    if (event?.status === 'complete' && onComplete) {
      onComplete(event.payload ?? {});
    }
    if (event?.status === 'failed' && onFailed) {
      const error =
        (event.payload as Record<string, unknown>)?.error?.toString() ??
        'Generation failed';
      onFailed(error);
    }
  }, [event?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!event) return null;

  const { status, payload } = event;

  if (status === 'complete') {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
        <CheckCircleOutlineIcon color="success" />
        <Typography variant="body2" color="success.main">
          Generation complete
        </Typography>
      </Stack>
    );
  }

  if (status === 'failed') {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        {(payload as Record<string, unknown>)?.error?.toString() ??
          'Generation failed'}
      </Alert>
    );
  }

  const step = payload?.step ?? 'Processing...';
  const current = payload?.current;
  const total = payload?.total;
  const detailLabel = payload?.detailLabel;
  const hasProgressBar = current != null && total != null && total > 0;

  const elapsedLabel = elapsedSeconds > 0 ? `${elapsedSeconds}s elapsed` : '';

  if (hasProgressBar) {
    const percent = Math.round(((current ?? 0) / total) * 100);
    return (
      <Box sx={{ py: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="body2" fontWeight={500}>
            {step}
            {detailLabel ? `: ${detailLabel}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {elapsedLabel}
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={percent} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {current} of {total} ({percent}%)
        </Typography>
      </Box>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
      <CircularProgress size={20} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">{step}</Typography>
        {elapsedLabel && (
          <Typography variant="caption" color="text.secondary">
            {elapsedLabel}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
